import { defineStore } from 'pinia';
import { type IStrapiImage } from '~/types/strapiImage';
import { assignDataToFields, createPopulateString } from '~/utils/strapiComponents/addStrapiData';
import { strapi } from '../transport/strapi';

export const useStoreComponents = defineStore('components', {
  state: () => ({
    timer: {
      daysTitle: '',
      finishedTitle: '',
      expiresInTitle: '',
      hoursTitle: '',
      leftTitle: '',
      minutesTitle: '',
      secondsTitle: '',
      willStartTitle: '',
      yearsTitle: '',
      monthsTitle: '',
      availableTitle: '',
    },
    placeholders: {
      bonusCode: '',
      copyAddressTitle: '',
      copyTagTitle: '',
      freespins: '',
      wager: '',
    },
    notifications: {
      fromTitle: '',
      newTitle: '',
      archiveTitle: '',
      noNotificationsTitle: '',
      showAllNotificationsTitle: '',
      verifyEmailTitle: '',
      vipLevelUpdateTitle: '',
      freespinNoGamesTitle: '',
      freespinContactUsTitle: '',
      emailSentTitle: '',
      emailSentText: '',
      cancelBonusTitle: '',
      cancelBonusText: '',
    },
    tooltips: {
      copiedIntoBuffer: '',
      converterTitle: '',
      languagesTitle: '',
    },
    errorsMessages: {
      emailNotExistError: '',
      blankError: '',
      less10symbolsError: '',
      lessThan18Error: '',
      cardNumberMinLength: '',
      expiryDateText: '',
      unknownError: '',
      onlyNumbers: '',
      amountPrecisionError: '',
      missedCaptcha: '',
      requiredCheckbox: '',
      required: '',
      passwordWeak: '',
      passwordMedium: '',
      passwordStrong: '',
      passwordText: '',
      noSpacesAllowed: '',
      onlyLetters: '',
      minLengthText: '',
      characterText: '',
      noValidEmailAddress: '',
      bonusCodeError: '',
      errorMessageCheckbox: '',
      sameAs: '',
      maxLength: '',
      expiryDateTextInvalid: '',
    },
    search: {
      searchResult: '',
      noFoundText: '',
      searchTitle: '',
      searchShowMore: '',
      searchHelpText: '',
      searchProviderTitle: '',
      searchFilterProviderTitle: '',
      searchFilterProviderAllTitle: '',
      searchFilterProviderResetTitle: '',
      tabGamesText: '',
      tabProvidersText: '',
      hitsText: '',
      hitsIcon: {} as IStrapiImage,
      hitsForProviderText: '',
      gameNotAvailableTitle: '',
      gameBlockedByBonusTitle: '',
    },
    races: {
      leaderboardTitle: '',
      levelTitle: '',
      placeTitle: '',
      playerTitle: '',
      prizeTitle: '',
      prizePoolTitle: '',
      mechanicsBetTitle: '',
      mechanicsWinTitle: '',
      mechanicsRateTitle: '',
      mechanicsSpinTitle: '',
      mechanicsPointsTitle: '',
      mechanicsBetDescriptionText: '',
      mechanicsSpinDescriptionText: '',
      mechanicsPointsDescriptionText: '',
      mechanicsWinDescriptionText: '',
      mechanicsRateDescriptionText: '',
      myPlaceTitle: '',
      congratulationsMessageText: '',
      gameTitle: '',
      minimumBetTitle: '',
      mechanicsTitle: '',
      raceLabelWeeklyTitle: '',
      raceLabelDailyTitle: '',
      raceLabelExclusiveTitle: '',
      raceLabelComingSoonTitle: '',
      raceLabelEventTitle: '',
      raceLabelPartnerTitle: '',
      networkRaceLeaderboardTitle: '',
      networkRaceLeaderboardText: '',
      noMinBetTitle: '',
      iconJoined: {} as IStrapiImage,
      customNetworkTitleNumOfPrizes: '',
      customNetworkTitleSum: '',
      leaderboardYou: '',
      rulesTitle: '',
      nothingFoundInGameSearch: '',
      howParticipateText: '',
    },
    bonuses: {
      recievedBonusIcon: {} as IStrapiImage,
      lockedBonusIcon: {} as IStrapiImage,
      missedBonusIcon: {} as IStrapiImage,
      allPromoTabTitle: '',
      promoSelectTitle: '',
      bonusRecievedTitle: '',
      bonusLockedTitle: '',
      bonusMissedTitle: '',
      bonusSubTitleComingSoon: '',
      bonusSubTitle: '',
      allPromoTabIcon: {} as IStrapiImage,
      bonusGamesTitle: '',
      bonusInfoBtn: '',
      labelIssued: '',
      labelActivated: '',
      lockedBonusTitle: '',
      lockedBonusText: '',
      bonusExpired: '',
    },
    sortProvider: {
      default: '',
      nameAlphabet: '',
      nameAlphabetRevers: '',
      random: '',
      gamesCountHigh: '',
      gamesCountLow: '',
      rating: '',
      topProviders: '',
      allProviders: '',
      titleSeo: '',
      textSeo: '',
      placeholderSort: '',
      placeholderInput: '',
      nameButtonShow: '',
      providerNotAvailableTitle: '',
      placeholderSorting: '',
      filterByText: '',
      itemsSelectedText: '',
      sortNew: '',
      btnClearAll: '',
    },
    infoDropdownAction: {
      titleActionHeader: '',
      qualificationIsFinished: '',
      successfullyParticipateAction: '',
      qualificationWinnerToFinalRound: '',
      startDateAction: '',
      endDateAction: '',
      mainBannerAction: {} as IStrapiImage,
      bentoJoinText: '',
      bentoTimerText: '',
      bentoPrizeTitle: '',
      bentoPrizeSum: '',
      statusUserJoinedToRound: '',
      statusUserLockedRound: '',
      statusUserNotPlaceInRound: '',
      statusUserWithPlaceTournament: '',
      statusUserInStartTournament: '',
      statusQualifiedUserFinalRound: '',
      statusUserPosition: '',
      statusUserPositionAndPrize: '',
      statusUserWaitingFinalRound: '',
      changeStatusUserWaitingFinalRoundText: '',
      changeStatusUserWaitingFinalRound: false,
    },
    fbSignUp: {
      headTitle: '',
      headSubTitle: '',
      backgroundFbSignup: {} as IStrapiImage,
    },
    sidebar: {
      casinoHelpTitle: '',
      casinoHelpLinks: [] as { title: string; link: string; icon?: IStrapiImage }[],
    },
    bonusCustomImages: [] as { bonusTitle: string; image: IStrapiImage }[],
    requestPromise: null as Promise<any> | null,
  }),

  actions: {
    async loadComponents() {
      if (!this.requestPromise) {
        const populateString = createPopulateString([
          'notifications',
          'search',
          'search.hitsIcon',
          'races',
          'races.iconJoined',
          'bonuses',
          'bonuses.recievedBonusIcon',
          'bonuses.missedBonusIcon',
          'bonuses.lockedBonusIcon',
          'bonuses.allPromoTabIcon',
          'bonusCustomImages',
          'bonusCustomImages.image',
          'sortProvider',
          'infoDropdownAction',
          'infoDropdownAction.mainBannerAction',
          'timer',
          'placeholders',
          'tooltips',
          'errorsMessages',
          'fbSignUp',
          'fbSignUp.backgroundFbSignup',
          'sidebar',
          'sidebar.casinoHelpLinks',
          'sidebar.casinoHelpLinks.icon',
          'fortuneWheel.wheeltems',
          'fortuneWheel.wheeltems.image',
        ]);

        this.requestPromise = strapi()
          .get('component', populateString)
          .finally(() => (this.requestPromise = null));
      }

      const response = await this.requestPromise;

      if (response?.data) {
        assignDataToFields(this, response.data);

        return true;
      } else {
        return false;
      }
    },
  },
});
